import { Injectable } from '@angular/core';
import { firstValueFrom, Observable, throwError } from 'rxjs';
import { catchError, tap, map } from 'rxjs/operators';

import {
    SellerOrderSummaryDTO,
    UserProfileDTO,
    UserProfileClient,
    DeliveryPlanClient,
    OrderClient,
    CreateDeliveryPlanDTO,
    UpdateDeliveryStatusDTO,
    DeliveryPlanDTO,
    FilterDTO,
    FilterOperator,
    UserAccountClient,
    UserInfoDTO
} from 'src/app/base/models/models.swagger.generated';
import { OrderService } from '../order.service';

import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { ToastService } from 'src/app/base/services/toast.service';
import { ErrorType } from 'src/app/core/routing/error-types';

@Injectable()
export class DeliveryDetailsService {

    constructor(
        private orderService: OrderService,
        private orderClient: OrderClient,
        private userProfileClient: UserProfileClient,
        private deliveryPlanClient: DeliveryPlanClient,
        private userAccountClient: UserAccountClient,
        private spinner: SpinnerService,
        private toast: ToastService
    ) { }

    public async getCurrentUserProfile(): Promise<UserProfileDTO | null> {
        try {
            this.spinner.showSpinner = true;
            const profile = await firstValueFrom(this.userProfileClient.get()) as UserProfileDTO;
            this.spinner.showSpinner = false;
            return profile || null;
        } catch (err) {
            this.spinner.showSpinner = false;
            this.handleError('Error loading user profile');
            throw err;
        }
    }

    public async getUserDetailsById(userId: string): Promise<UserProfileDTO | null> {
        try {
            return await firstValueFrom(this.userProfileClient.getById(userId)) as UserProfileDTO;
        } catch (err) {
            console.error(`Failed to fetch user ${userId}:`, err);
            return null;
        }
    }

    public getSellerOrders(): Observable<SellerOrderSummaryDTO[]> {
        return this.orderService.getSellerOrders();
    }

    public getEligibleDeliveryOrders(query: {
        sellerProfileId?: number;
        search?: string;
        status?: number;
        assignment?: string;
        eligibleOnly?: boolean;
        skip?: number;
        take?: number;
        from?: string;
        to?: string;
    }): Observable<{ items: any[]; totalNumberOfItems: number }> {
        return this.orderClient.getEligibleDeliveryOrders(
            query.sellerProfileId,
            query.search,
            query.status,
            query.assignment,
            query.eligibleOnly,
            query.skip,
            query.take,
            query.from,
            query.to
        );
    }



    public getDeliveryExecutives(): Observable<any[]> {
        this.spinner.showSpinner = true;
        return this.userAccountClient.getDeliveryExecutives().pipe(
            tap(() => this.spinner.showSpinner = false),
            map(users => users || []),
            catchError(err => {
                this.spinner.showSpinner = false;
                this.handleError('Error loading delivery executives');
                return throwError(() => err);
            })
        );
    }

    public createDeliveryPlan(dto: CreateDeliveryPlanDTO): Observable<DeliveryPlanDTO> {
        this.spinner.showSpinner = true;
        return this.deliveryPlanClient.createDeliveryPlan(dto).pipe(
            tap(() => {

                this.spinner.showSpinner = false;
            }),
            catchError(err => {
                this.spinner.showSpinner = false;
                this.handleError('Error creating delivery plan');
                return throwError(() => err);
            })
        );
    }

    public updateDeliveryPlan(id: number, dto: UpdateDeliveryStatusDTO): Observable<DeliveryPlanDTO> {
        this.spinner.showSpinner = true;
        return this.deliveryPlanClient.updateDeliveryPlan(id, dto).pipe(
            tap(() => {

                this.spinner.showSpinner = false;
            }),
            catchError(err => {
                this.spinner.showSpinner = false;
                this.handleError('Error updating delivery plan');
                return throwError(() => err);
            })
        );
    }

    public getDeliveryPlan(id: number): Observable<DeliveryPlanDTO> {
        this.spinner.showSpinner = true;
        return this.deliveryPlanClient.getDeliveryPlan(id, null, null).pipe(
            tap(() => this.spinner.showSpinner = false),
            catchError(err => {
                this.spinner.showSpinner = false;
                this.handleError('Error loading delivery plan');
                return throwError(() => err);
            })
        );
    }

    public searchDeliveryPlans(): Observable<any> {
        this.spinner.showSpinner = true;
        return this.deliveryPlanClient.searchDeliveryPlans([], 0, 1000, 'name', 0).pipe(
            tap(() => this.spinner.showSpinner = false),
            catchError(err => {
                this.spinner.showSpinner = false;
                this.handleError('Error loading delivery plans');
                return throwError(() => err);
            })
        );
    }

    public deleteDeliveryPlan(id: number): Observable<void> {
        this.spinner.showSpinner = true;
        return this.deliveryPlanClient.deleteDeliveryPlan(id).pipe(
            tap(() => {

                this.spinner.showSpinner = false;
            }),
            catchError(err => {
                this.spinner.showSpinner = false;
                this.handleError('Error deleting delivery plan');
                return throwError(() => err);
            })
        );
    }

    public addOrderToDeliveryPlan(orderId: number, planName: string): void {

    }

    public removeOrderFromDeliveryPlan(): void {

    }

    public submitDeliveryPlan(): void {

    }

    private handleError(message: string = 'An error occurred') {
        if (!this.toast.doesErrorExistAlready(ErrorType.serverError)) {
            this.toast.showError(
                'Error',
                message,
                undefined,
                ErrorType.serverError
            );
        }
    }
}